import { isPresent } from '@ember/utils';
import { type ModelRegistry } from 'ember-data/types/registries/model';
import {
  BDC_COUNTRY_CURRENCIES,
  UNSUPPORTED_PROPERTY_COUNTRY_CODES,
  UNSUPPORTED_PROPERTY_STATES,
} from 'uplisting-frontend/utils/bdc-constants';

export type BdcCurrencyStatus =
  | 'valid'
  | 'currency_mismatch'
  | 'unresolved_country'
  | 'unsupported_country';

export interface IBdcCurrencyCheck {
  status: BdcCurrencyStatus;
  countryCode?: string;
  acceptedCurrencies: string[];
}

export type BdcAddressStatus =
  | 'valid'
  | 'incomplete'
  | 'unsupported_country'
  | 'unsupported_state';

export interface IBdcAddressCheck {
  status: BdcAddressStatus;
  countryCode?: string;
}

export function isValidBasics(property: ModelRegistry['property']): boolean {
  return (
    isPresent(property.maximumCapacity) &&
    isPresent(property.type) &&
    isPresent(property.bedrooms) &&
    isPresent(property.beds) &&
    isPresent(property.bathrooms) &&
    isPresent(property.bathroomsType)
  );
}

export function isValidBaseRate(property: ModelRegistry['property']): boolean {
  return !!property.baseRate?.id;
}

export function isValidPhotos(property: ModelRegistry['property']): boolean {
  return property.hasMany('photos').ids().length >= 6;
}

export function isValidBdcPhotos(property: ModelRegistry['property']): boolean {
  return property.hasMany('photos').ids().length >= 10;
}

export function isValidHeadline(property: ModelRegistry['property']): boolean {
  const length = property.homeawayLink?.headline?.length ?? 0;

  return 20 <= length && length <= 80;
}

export function isValidPropertyType(
  property: ModelRegistry['property'],
): boolean {
  return property.type?.length > 0;
}

export function isValidDescription(
  property: ModelRegistry['property'],
): boolean {
  const length = property.homeawayLink?.description?.length ?? 0;

  return 400 <= length && length <= 10000;
}

export function isValidPaymentMethod(
  property: ModelRegistry['property'],
): boolean {
  return !!property.stripeConnection?.id;
}

export function isValidTaxes(property: ModelRegistry['property']): boolean {
  const fee = property.propertyFee;

  if (!fee) {
    return false;
  }

  return (
    fee.perBookingPercentageTax > 0 ||
    fee.perBookingAmountTax > 0 ||
    fee.perPersonPerNightTax > 0 ||
    fee.perNightTax > 0
  );
}

export function isValidAddress(property: ModelRegistry['property']): boolean {
  const { address } = property;

  return (
    isPresent(address) &&
    isPresent(address.city) &&
    isPresent(address.country) &&
    isPresent(address.latitude) &&
    address.latitude !== 0 &&
    address.latitude >= -90 &&
    address.latitude <= 90 &&
    isPresent(address.longitude) &&
    address.longitude !== 0 &&
    address.longitude >= -180 &&
    address.longitude <= 180
  );
}

export function isValidCapacity(property: ModelRegistry['property']): boolean {
  return isPresent(property.maximumCapacity);
}

export function isValidBathrooms(property: ModelRegistry['property']): boolean {
  return isPresent(property.bathrooms) && isPresent(property.bathroomsType);
}

export function isValidDirectBookingType(
  property: ModelRegistry['property'],
): boolean {
  return property.instantBookable;
}

export function isPublishableOnHomeaway(
  property: ModelRegistry['property'],
): boolean {
  return (
    isValidBasics(property) &&
    isValidBaseRate(property) &&
    isValidPhotos(property) &&
    isValidPropertyType(property) &&
    isValidHeadline(property) &&
    isValidDescription(property) &&
    isValidPaymentMethod(property)
  );
}

export function isPublishableOnGoogleAri(
  property: ModelRegistry['property'],
): boolean {
  return (
    isValidAddress(property) &&
    isValidBaseRate(property) &&
    isValidCapacity(property) &&
    isValidDirectBookingType(property) &&
    isValidPaymentMethod(property)
  );
}

export function checkBdcCurrency(
  property: ModelRegistry['property'],
): IBdcCurrencyCheck {
  const currency = property.currency?.toUpperCase() ?? '';
  const countryCode = property.address?.countryCode || undefined;
  const acceptedCurrencies = countryCode
    ? (BDC_COUNTRY_CURRENCIES[countryCode] ?? [])
    : [];

  if (!countryCode) {
    return { status: 'unresolved_country', acceptedCurrencies };
  }

  if (acceptedCurrencies.length === 0) {
    return { status: 'unsupported_country', countryCode, acceptedCurrencies };
  }

  if (acceptedCurrencies.includes(currency)) {
    return { status: 'valid', countryCode, acceptedCurrencies };
  }

  return { status: 'currency_mismatch', countryCode, acceptedCurrencies };
}

export function checkBdcAddress(
  property: ModelRegistry['property'],
): IBdcAddressCheck {
  const countryCode = property.address?.countryCode || undefined;

  if (UNSUPPORTED_PROPERTY_COUNTRY_CODES.includes(countryCode ?? '')) {
    return { status: 'unsupported_country', countryCode };
  }

  if (
    UNSUPPORTED_PROPERTY_STATES.includes(property.address?.state?.trim() ?? '')
  ) {
    return { status: 'unsupported_state', countryCode };
  }

  if (!isValidAddress(property)) {
    return { status: 'incomplete', countryCode };
  }

  return { status: 'valid', countryCode };
}

export function propertyDisplayName(
  property: ModelRegistry['property'] | undefined,
  multiUnit: ModelRegistry['multi-unit'] | undefined,
): string {
  const nickname = property?.nickname ?? '';

  if (multiUnit) {
    return [multiUnit.name, nickname].filter(Boolean).join(' - ');
  }

  return nickname;
}
